import { useEffect } from 'react';
import { bumpShortcutUsage } from '@/lib/shortcutAnalytics';

interface KeyboardShortcutHandlers {
  onSave?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onDelete?: () => void;
  onEscape?: () => void;
  onShowHelp?: () => void;
  /**
   * When true AND the app is running in an iframe, global shortcuts
   * (Ctrl/Cmd+S/Z/Y, Delete) are suppressed so the host page's bindings
   * win. Escape still fires because it's the universal dismiss gesture.
   * Default: false (shortcuts are active regardless of embedding).
   */
  suppressInIframe?: boolean;
}

function isEmbedded(): boolean {
  try { return window.self !== window.top; } catch { return true; }
}

export function useKeyboardShortcuts({
  onSave,
  onUndo,
  onRedo,
  onDelete,
  onEscape,
  onShowHelp,
  suppressInIframe = false,
}: KeyboardShortcutHandlers) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

      // Allow Escape even in inputs and always — universal dismiss.
      if (e.key === 'Escape') {
        bumpShortcutUsage('escape');
        onEscape?.();
        return;
      }

      // Don't trigger shortcuts when typing in form fields
      if (isInput) return;

      // "?" opens the cheatsheet. `?` is Shift+/ on most layouts, so
      // accept both e.key === '?' and the physical key + shift combo.
      if (!e.ctrlKey && !e.metaKey && !e.altKey && (e.key === '?' || (e.shiftKey && e.key === '/'))) {
        e.preventDefault();
        bumpShortcutUsage('help');
        onShowHelp?.();
        return;
      }

      // Suppress in iframe if opted-in (lets host page own Ctrl+S etc).
      if (suppressInIframe && isEmbedded()) return;

      const isCtrlOrMeta = e.ctrlKey || e.metaKey;

      if (isCtrlOrMeta && e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        bumpShortcutUsage('redo');
        onRedo?.();
      } else if (isCtrlOrMeta && e.key === 'y') {
        e.preventDefault();
        bumpShortcutUsage('redo');
        onRedo?.();
      } else if (isCtrlOrMeta && e.key === 's') {
        e.preventDefault();
        bumpShortcutUsage('save');
        onSave?.();
      } else if (isCtrlOrMeta && e.key === 'z') {
        e.preventDefault();
        bumpShortcutUsage('undo');
        onUndo?.();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        bumpShortcutUsage('delete');
        onDelete?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onSave, onUndo, onRedo, onDelete, onEscape, onShowHelp, suppressInIframe]);
}
